import React, { useState, Fragment } from 'react';
import { Row, Col, Button, Tooltip, Form, Input, Table, Radio, Empty, Tag, Pagination, Spin } from 'antd';
import moment from 'moment';
import { useTranslation } from 'react-i18next';

const getAptitudeColor = (status) => {
  if (!status) return 'default';
  const lowerStatus = status.toLowerCase();

  const greenMatches = ['approved', 'eligible', 'eligible_suggestions'];
  const warningMatches = ['reproved_temporally', 'temporary_reproved'];
  const redMatches = ['reproved', 'permantent_reproved', 'not_eligible', 'revoked'];

  if (greenMatches.some((keyword) => lowerStatus.includes(keyword))) {
    return 'green';
  }
  if (warningMatches.some((keyword) => lowerStatus.includes(keyword))) {
    return 'warning';
  }
  if (redMatches.some((keyword) => lowerStatus.includes(keyword))) {
    return 'red';
  }
  if (lowerStatus === 'evaluated') {
    return 'blue';
  }
  if (lowerStatus === 'pending') {
    return 'default';
  }
  return 'default';
};

const PatientTablePeru = ({
  patients,
  onPageChange,
  deletePatient,
  changeReservationNumber,
  roles = [],
  loading,
  fetchAdmissionPatientPrestation,
  prestations,
  deletePatientExams,
}) => {
  const { t } = useTranslation();
  const [openDeleteTooltipId, setOpenDeleteTooltipId] = useState(null);
  const canDelete = roles.includes('branch_office_manager') || roles.includes('direccion_medica');
  const columns = [
    {
      title: 'Cita',
      dataIndex: 'number',
      key: 'number',
      width: 120,
      fixed: 'left',
    },
    {
      title: 'Nombres',
      dataIndex: 'patient_full_name',
      key: 'patient_full_name',
      width: 200,
      fixed: 'left',
    },
    {
      title: 'Documento',
      dataIndex: 'document_number',
      key: 'document_number',
      width: 120,
      fixed: 'left',
    },
    {
      title: 'Sede',
      dataIndex: 'branch_office_name',
      key: 'branch_office_name',
      width: 150,
      fixed: 'left',
      render: text => text || '-',
    },
    {
      title: 'Empresa',
      dataIndex: 'business_name',
      key: 'business_name',
      width: 180,
      fixed: 'left',
      render: text => text || '-',
    },
    {
      title: 'Subcont.',
      dataIndex: 'outsourcing_name',
      key: 'outsourcing_name',
      width: 180,
      render: text => text || '-',
    },
    {
      title: 'Tercera',
      dataIndex: 'third_company_name',
      key: 'third_company_name',
      width: 180,
      render: text => text || '-',
    },
    {
      title: 'Tipo Evaluación',
      dataIndex: 'evaluation_type_name',
      key: 'evaluation_type_name',
      width: 150,
      render: text => text || '-',
    },
    {
      title: 'Protocolo',
      dataIndex: 'protocol_name',
      key: 'protocol_name',
      width: 180,
      render: (text, patientRecord) => {
        if (!text || text === '-') return '-';
        return (
          <Tooltip
            trigger="click"
            overlayClassName="patient-services-tooltip"
            onClick={() => fetchAdmissionPatientPrestation({ admission_patient_id: patientRecord.admission_patient_id })}
            color="#FFFFFF"
            title={
              <Fragment>
                {prestations && prestations.length > 0 ? (
                  <Fragment>
                    <Row justify="space-between">
                      <Col lg={18}>
                        <p className="title mb-10">
                          {t('admissionPatientPage.patientTable.service')}
                        </p>
                      </Col>
                      <Col lg={6}>
                        <p className="title text-center mb-10">Eliminar</p>
                      </Col>
                    </Row>
                    <Fragment>
                      {loading.detail ? (
                        <div className="text-center">
                          <Spin className="custom-spinner" />
                        </div>
                      ) : (
                        prestations.map((prestation, prestationIndex) => {
                          return (
                            <Row justify="space-between" key={prestationIndex}>
                              <Col lg={18}>
                                <p>{prestation.type_name}</p>
                              </Col>
                              <Col lg={6} className="button-wrapper">
                                <Button
                                  className="delete-icon-button"
                                  onClick={() => deletePatientExams(prestation)}
                                >
                                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 148.61 189.36">
                                    <g id="Capa_2" dataname="Capa 2">
                                      <g id="Capa_1-2" dataname="Capa 1">
                                        <path d="M47.16,21.25c0-3.94-.14-7.24,0-10.53C47.49,4.64,51.6.29,57.66.17q16.5-.33,33,0c6.37.11,10.48,4.42,10.78,10.79.15,3.28,0,6.58,0,10.4,11.53,0,22.48-.06,33.43,0,7.49,0,12.84,3.93,13.53,10a92.45,92.45,0,0,1,.07,10.16H.13c0-3.65-.34-7.14.08-10.54.69-5.58,5.89-9.6,12.18-9.65C23.71,21.19,35,21.25,47.16,21.25Zm46.89-.2c0-3.38,0-6.19,0-9C94,9,92.36,7.48,89.32,7.48c-10,0-20-.16-29.93.17-1.52,0-3.93,2.14-4.25,3.64-.67,3.1-.2,6.43-.2,9.76Z" />
                                        <path d="M15.42,54.5v4.63q0,58.15,0,116.31c0,10.25,4.24,13.91,16.09,13.91q42.55,0,85.09,0c12.36,0,16.25-3.36,16.25-14.11q0-57.93,0-115.88V54.5Z" />
                                      </g>
                                    </g>
                                  </svg>
                                </Button>
                              </Col>
                            </Row>
                          );
                        })
                      )}
                    </Fragment>
                  </Fragment>
                ) : (
                  <Empty
                    description={
                      <span style={{ fontSize: 12 }}>
                        {t('admissionPatientPage.patientTable.emptyDisclaimer')}
                      </span>
                    }
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                  />
                )}
              </Fragment>
            }
          >
            <span className="clickable-underlined-text">{text}</span>
          </Tooltip>
        );
      },
    },
    {
      title: 'Aptitud',
      dataIndex: 'consultation_result_text',
      key: 'consultation_result_text',
      width: 120,
      render: (text, record) => {
        if (!text) return '-';
        const color = getAptitudeColor(record.consultation_result_status);
        return <Tag color={color}>{text}</Tag>;
      },
    },
    {
      title: 'Observaciones',
      dataIndex: 'reevaluation_exam_names',
      key: 'reevaluation_exam_names',
      width: 250,
      render: (text, patientRecord) => {
        if (!text && !patientRecord.observation_lift_specialty_names) return '-';
        return (
          <div className="observations-cell">
            {text && (
              <div className="observation-entry">
                <Tag className="cmt-tag cmt-tag-outline-secondary">{t('admissionPatientPage.patientTable.reevaluation')}</Tag>
                <span className="observation-detail">{text}</span>
              </div>
            )}
            {patientRecord.observation_lift_specialty_names && (
              <div className="observation-entry">
                <Tag className="cmt-tag cmt-tag-outline-warning">{t('admissionPatientPage.patientTable.observationLift')}</Tag>
                <span className="observation-detail">{patientRecord.observation_lift_specialty_names}</span>
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: 'Inicio',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 140,
      render: text => text ? moment(text).format('DD/MM/YYYY HH:mm') : '-',
    },
    {
      title: 'Término',
      dataIndex: 'done_at',
      key: 'done_at',
      width: 140,
      render: text => text ? moment(text).format('DD/MM/YYYY HH:mm') : '-',
    },
    // {
    //   title: 'Acción',
    //   key: 'action',
    //   width: 80,
    //   fixed: 'right',
    //   align: 'center',
    //   render: (text, patient) => {
    //     if (!canDelete) {
    //       return (
    //         <div className="button-wrapper d-flex justify-center">
    //           <Tooltip title="No tienes permisos para eliminar este ingreso">
    //             <svg className="disabled-delete-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 148.61 189.36">
    //               <g id="Capa_2" dataname="Capa 2">
    //                 <g id="Capa_1-2" dataname="Capa 1">
    //                   <path d="M47.16,21.25c0-3.94-.14-7.24,0-10.53C47.49,4.64,51.6.29,57.66.17q16.5-.33,33,0c6.37.11,10.48,4.42,10.78,10.79.15,3.28,0,6.58,0,10.4,11.53,0,22.48-.06,33.43,0,7.49,0,12.84,3.93,13.53,10a92.45,92.45,0,0,1,.07,10.16H.13c0-3.65-.34-7.14.08-10.54.69-5.58,5.89-9.6,12.18-9.65C23.71,21.19,35,21.25,47.16,21.25Zm46.89-.2c0-3.38,0-6.19,0-9C94,9,92.36,7.48,89.32,7.48c-10,0-20-.16-29.93.17-1.52,0-3.93,2.14-4.25,3.64-.67,3.1-.2,6.43-.2,9.76Z" />
    //                   <path d="M15.42,54.5v4.63q0,58.15,0,116.31c0,10.25,4.24,13.91,16.09,13.91q42.55,0,85.09,0c12.36,0,16.25-3.36,16.25-14.11q0-57.93,0-115.88V54.5Z" />
    //                 </g>
    //               </g>
    //             </svg>
    //           </Tooltip>
    //         </div>
    //       );
    //     }

    //     const rowUniqueId = `${patient.admission_patient_id}_${patient.order_detail_id || 'base'}`;
    //     return (
    //       <div className="button-wrapper d-flex justify-center">
    //         <Tooltip
    //           trigger="click"
    //           color='#FFFFFF'
    //           overlayClassName='delete-admission-tooltip'
    //           autoAdjustOverflow
    //           open={openDeleteTooltipId === rowUniqueId}
    //           onOpenChange={(open) => setOpenDeleteTooltipId(open ? rowUniqueId : null)}
    //           title={
    //             <Row justify='center' gutter={[0, 10]}>
    //               <Col lg={24} xs={24}>
    //                 <p className='text-center mb-0'>¿Estás seguro que deseas eliminar?</p>
    //               </Col>
    //               <Col lg={24} xs={24}>
    //                 <Row justify='center' gutter={10}>
    //                   <Col>
    //                     <Button
    //                       className='cmt-button secondary outline'
    //                       size='small'
    //                       onClick={() => setOpenDeleteTooltipId(null)}
    //                     >
    //                       Cancelar
    //                     </Button>
    //                   </Col>
    //                   <Col>
    //                     <Button
    //                       className='cmt-button primary'
    //                       type='primary'
    //                       size='small'
    //                       onClick={() => {
    //                         deletePatient(patient);
    //                         setOpenDeleteTooltipId(null);
    //                       }}
    //                     >
    //                       Confirmar
    //                     </Button>
    //                   </Col>
    //                 </Row>
    //               </Col>
    //             </Row>
    //           }
    //         >
    //           <svg className="active-delete-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 148.61 189.36">
    //             <g id="Capa_2" dataname="Capa 2">
    //               <g id="Capa_1-2" dataname="Capa 1">
    //                 <path d="M47.16,21.25c0-3.94-.14-7.24,0-10.53C47.49,4.64,51.6.29,57.66.17q16.5-.33,33,0c6.37.11,10.48,4.42,10.78,10.79.15,3.28,0,6.58,0,10.4,11.53,0,22.48-.06,33.43,0,7.49,0,12.84,3.93,13.53,10a92.45,92.45,0,0,1,.07,10.16H.13c0-3.65-.34-7.14.08-10.54.69-5.58,5.89-9.6,12.18-9.65C23.71,21.19,35,21.25,47.16,21.25Zm46.89-.2c0-3.38,0-6.19,0-9C94,9,92.36,7.48,89.32,7.48c-10,0-20-.16-29.93.17-1.52,0-3.93,2.14-4.25,3.64-.67,3.1-.2,6.43-.2,9.76Z" />
    //                 <path d="M15.42,54.5v4.63q0,58.15,0,116.31c0,10.25,4.24,13.91,16.09,13.91q42.55,0,85.09,0c12.36,0,16.25-3.36,16.25-14.11q0-57.93,0-115.88V54.5Z" />
    //               </g>
    //             </g>
    //           </svg>
    //         </Tooltip>
    //       </div>
    //     );
    //   }
    // },
  ];

  return (
    <Col lg={24}>
      {patients?.data?.length > 0 ? (
        <Row justify="center" className="patient-table mt-30">
          <Col lg={23} xs={24} className="mb-30">
            <Table
              columns={columns}
              dataSource={patients.data}
              rowKey={(record) => `${record.admission_patient_id}_${record.order_detail_id || 'base'}`}
              pagination={false}
              scroll={{ x: 'max-content', y: 'calc(100vh - 380px)' }}
              size="middle"
              bordered
            />

            <Row justify="end" className="table-pagination mt-20">
              <Col>
                <Pagination
                  current={patients?.pagination?.currentPage || 1}
                  pageSize={patients?.pagination?.perPage || 30}
                  total={patients?.pagination?.total || 0}
                  onChange={onPageChange}
                  showSizeChanger={false}
                  showTotal={(totalCount, rangeRange) => `${rangeRange[0]}-${rangeRange[1]} de ${totalCount} registros`}
                />
              </Col>
            </Row>
          </Col>
        </Row>
      ) : (
        <Empty className="mt-50 mb-50" description="No hay pacientes ingresados" />
      )}
    </Col>
  );
};

export default PatientTablePeru;
